// src/pages/Techniques/TechniquesView.jsx
import React, { useState, useEffect, useMemo } from "react";
import TechniquesCard from "./TechniquesCard";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { getAllTechniques } from "../../services/techniquesServices";
import { FaSearch, FaFilter } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const BELTS = ["Jaune", "Orange", "Verte", "Bleue", "Marron", "Noire"];
const FAMILLES = [
  "Jambe",
  "Hanche",
  "Epaule",
  "Bras",
  "Sutemi Waza",
  "Immobilisation",
  "Shime Waza",
  "Kansetsu-Waza",
  "Self Défense",
  "Kata",
  "Vocabulaire",
  "Les Bases",
  "Ukemi",
];

const UV2_LIST = [
  "1A",
  "1B",
  "1C",
  "1D",
  "1E",
  "1F",
  "1G",
  "1H",
  "1I",
  "1J",
  "1K",
  "1L",
  "1M",
  "1N",
  "1O",
  "1P",
  "1Q",
];

const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const TechniquesView = () => {
  useScrollToTop();
  const [techniques, setTechniques] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCeinture, setSelectedCeinture] = useState(
    "Toutes les ceintures"
  );
  const [selectedFamille, setSelectedFamille] = useState("Toutes les familles");
  const [selectedUV2, setSelectedUV2] = useState("Toutes les fiches");
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id); // prêt pour évolutions si besoin

  useEffect(() => {
    const fetchTechniques = async () => {
      try {
        const fetchedTechniques = await getAllTechniques();
        setTechniques(fetchedTechniques || []);
      } catch (e) {
        console.error("Erreur lors du chargement des techniques:", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTechniques();
  }, []);

  const handleCeintureChange = (e) => setSelectedCeinture(e.target.value);
  const handleFamilleChange = (e) => setSelectedFamille(e.target.value);
  const handleUV2Change = (e) => setSelectedUV2(e.target.value);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCeinture("Toutes les ceintures");
    setSelectedFamille("Toutes les familles");
    setSelectedUV2("Toutes les fiches");
  };

  // 👉 On NE trie plus : on garde l'ordre d'origine de la base
  const filteredTechniques = useMemo(() => {
    const q = normalize(searchTerm);

    return (techniques || []).filter((t) => {
      const nom = t.nom || "";
      const trad = t.traduction || "";
      const famille = t.famille || "";
      const uv2 = t.uv2 || "";

      const matchSearch =
        !q ||
        normalize(nom).includes(q) ||
        normalize(trad).includes(q) ||
        normalize(famille).includes(q);

      const matchBelt =
        selectedCeinture === "Toutes les ceintures" ||
        (t.ceinture && t.ceinture === selectedCeinture);

      const matchFamille =
        selectedFamille === "Toutes les familles" ||
        (t.famille && t.famille === selectedFamille);

      const matchUV2 =
        selectedUV2 === "Toutes les fiches" ||
        (uv2 && uv2.toString().includes(selectedUV2));

      return matchSearch && matchBelt && matchFamille && matchUV2;
    });
  }, [techniques, searchTerm, selectedCeinture, selectedFamille, selectedUV2]);

  const totalCount = techniques.length;
  const filteredCount = filteredTechniques.length;

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      <PageHeader
        eyebrow="Utilisateurs & Visiteurs"
        title="Techniques de Judo"
        subtitle="Révisez et explorez les techniques de Judo par ceinture, famille technique ou fiche UV2."
        size="lg"
        className="mb-6"
      />

      {/* Panneau filtres */}
      <div className="max-w-[1280px] mx-auto w-full mb-4">
        <div className="rounded-2xl bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 shadow-lg p-4 sm:p-5 space-y-4">
          {/* Ligne 1 : recherche + bouton reset + stats */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            {/* Recherche */}
            <div className="flex items-center gap-2 w-full md:max-w-md bg-slate-100 dark:bg-slate-800 rounded-full px-3 py-2">
              <FaSearch className="text-slate-500 dark:text-slate-300 text-sm" />
              <input
                type="text"
                className="bg-transparent border-none outline-none text-sm w-full placeholder:text-slate-400 dark:placeholder:text-slate-500"
                placeholder="Rechercher une technique (nom, traduction, famille)..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            {/* Stats + reset */}
            <div className="flex flex-col md:items-end gap-2">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                <FaFilter className="text-primary" />
                <span>
                  <span className="font-semibold">{filteredCount}</span>{" "}
                  technique(s) affichée(s) sur{" "}
                  <span className="font-semibold">{totalCount}</span>.
                </span>
              </div>
              <button
                type="button"
                onClick={resetFilters}
                className="self-start md:self-end inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Réinitialiser les filtres
              </button>
            </div>
          </div>

          {/* Ligne 2 : filtres ceinture / famille / UV2 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Ceintures */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Ceinture
              </span>
              <select
                className="w-full py-2 px-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/70 focus:border-primary"
                value={selectedCeinture}
                onChange={handleCeintureChange}
              >
                <option value="Toutes les ceintures">
                  Toutes les ceintures
                </option>
                {BELTS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            {/* Famille */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Famille technique
              </span>
              <select
                className="w-full py-2 px-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/70 focus:border-primary"
                value={selectedFamille}
                onChange={handleFamilleChange}
              >
                <option value="Toutes les familles">Toutes les familles</option>
                {FAMILLES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            {/* UV2 */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                UV2 (fiches grade)
              </span>
              <select
                className="w-full py-2 px-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/70 focus:border-primary"
                value={selectedUV2}
                onChange={handleUV2Change}
              >
                <option value="Toutes les fiches">Toutes les fiches UV2</option>
                {UV2_LIST.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Grid des cartes */}
      <div className="overflow-x-hidden mx-auto max-w-[1280px] w-full p-2 sm:p-4">
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-56 rounded-2xl bg-slate-100 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : filteredTechniques.length === 0 ? (
          <p className="text-sm text-center text-slate-600 dark:text-slate-300 italic mt-4">
            Aucune technique ne correspond à ces critères.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredTechniques.map((technique) => (
              <TechniquesCard key={technique.id} technique={technique} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TechniquesView;
